import * as React from 'react'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import CircularProgress from '@mui/material/CircularProgress'
import { useTranslation } from 'react-i18next'
import { useAttachments } from 'proposals/attachments/useAttachments'
import Attachments from 'proposals/attachments/Attachments'

type ProposalReadOnlyDetailsProps = {
  proposal: ProposalType
  fields: Field[]
}

const LabeledValue: React.FC<{ label: string; value: string | null | undefined; placeholder: string }> = ({
  label,
  value,
  placeholder
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
      {value && value.toString().trim().length > 0 ? value : placeholder}
    </Typography>
  </Box>
)

const ProposalReadOnlyDetails: React.FC<ProposalReadOnlyDetailsProps> = ({ proposal, fields }) => {
  const { t } = useTranslation()

  const {
    models: { images, documents, isImagesLoading, isDocumentsLoading }
  } = useAttachments(proposal.contextToken)

  const isAttachmentsLoading = isImagesLoading || isDocumentsLoading

  const valueByFieldId = React.useMemo(
    () =>
      proposal.fieldValues.reduce((memo, item) => {
        memo[item.fieldId] = item.value
        return memo
      }, {} as Record<string, string>),
    [proposal.fieldValues]
  )

  const sortedFields = React.useMemo(() => [...fields].sort((a, b) => a.order - b.order), [fields])

  const placeholder = t('not-answered')

  const formatLabel = (field: Field): string => {
    const unit = field.type === 'currency' ? field.currencyUnit ?? field.unitOfMeasure : field.unitOfMeasure
    return unit ? `${field.title} (${unit})` : field.title
  }

  return (
    <Box>
      <Typography color="primary" variant="h6" gutterBottom>
        {t('your-proposal')}
      </Typography>

      <Typography variant="body2" gutterBottom>
        {t('vendor-information')}
      </Typography>
      <Stack spacing={2} sx={{ mt: 1, mb: 3 }}>
        <LabeledValue
          label={t('company-name')}
          value={proposal.bidderCompanyName}
          placeholder={placeholder}
        />
        <LabeledValue
          label={t('business-id')}
          value={proposal.bidderBusinessId}
          placeholder={placeholder}
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 2
          }}
        >
          <LabeledValue
            label={t('contact-first-name')}
            value={proposal.bidderContactFirstName}
            placeholder={placeholder}
          />
          <LabeledValue
            label={t('contact-last-name')}
            value={proposal.bidderContactLastName}
            placeholder={placeholder}
          />
          <LabeledValue
            label={t('contact-email')}
            value={proposal.bidderContactEmail}
            placeholder={placeholder}
          />
          <LabeledValue
            label={t('contact-phone')}
            value={proposal.bidderContactPhone}
            placeholder={placeholder}
          />
        </Box>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      <Stack spacing={2}>
        {sortedFields.map((field) => {
          if (field.type === 'title') {
            return (
              <Box key={field.id} sx={{ mt: 1 }}>
                <Typography variant="body2">{field.title}</Typography>
                {field.description && (
                  <Typography color="text.secondary" sx={{ fontSize: 12 }}>
                    {field.description}
                  </Typography>
                )}
              </Box>
            )
          }

          return (
            <LabeledValue
              key={field.id}
              label={formatLabel(field)}
              value={valueByFieldId[field.id]}
              placeholder={placeholder}
            />
          )
        })}
      </Stack>

      <Box sx={{ mt: 4 }}>
        <Typography color="primary" variant="h6" gutterBottom>
          {t('attachments')}
        </Typography>
        {isAttachmentsLoading ? (
          <CircularProgress size={20} />
        ) : documents.length === 0 && images.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {t('no-attachments')}
          </Typography>
        ) : (
          <Attachments contextToken={proposal.contextToken} resourceWebLinks={[]} />
        )}
      </Box>
    </Box>
  )
}

export default ProposalReadOnlyDetails
